import { liveSessionEnabled } from "@constants/demo";
import { useSessionStore } from "@features/session";
import useAppConfigIds from "@hooks/useAppConfigIds";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { useQuery } from "@tanstack/react-query";
import { useLocalSearchParams } from "expo-router";
import { getReport } from "../api";

// The report shell stays mocked (app-level isMockedData), so the real reports
// query never runs on its own. Fire it here for live sessions: its response
// carries the per-item score/timeTaken for the analysis context and must
// resolve before the analysis stream is requested.
export const useProethicaReportGeneration = () => {
	const { id } = useLocalSearchParams<{ id: string }>();
	const sessionId = Number(id);
	const { userId } = useAppConfigIds();
	const demoExamId = useDemoExamStore((state) => state.currentExam?.id);
	const isAnswerEventInProgress = useSessionStore(
		(state) => state.isAnswerEventInProgress,
	);
	const isEndingSessionInProgress = useSessionStore(
		(state) => state.isEndingSessionInProgress,
	);

	const isLive = liveSessionEnabled(demoExamId);

	const {
		isSuccess,
		isError,
		data: report,
	} = useQuery({
		queryKey: ["session-report-generation", sessionId, userId],
		queryFn: () => getReport({ sessionId, userId }),
		// A finished session's report is immutable; a refetch would churn the
		// additionalContext memo and abort/restart the live analysis stream.
		staleTime: Number.POSITIVE_INFINITY,
		enabled:
			isLive &&
			!(isAnswerEventInProgress || isEndingSessionInProgress) &&
			Boolean(sessionId) &&
			Boolean(userId),
	});

	// A failed stats query is settled, not pending: the tiles it feeds fall back
	// to a placeholder instead of skeletoning forever.
	return {
		reportsReady: !isLive || isSuccess,
		reportStatsLoading: isLive && !(isSuccess || isError),
		report,
	};
};
